import Box from '@mui/joy/Box'
import * as Sentry from '@sentry/react'
import { type ComponentType, type FC, type ReactElement } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  createBrowserRouter,
  type LazyRouteFunction,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  useLocation,
} from 'react-router-dom'

import ChatwootLoader from 'COMPONENTS/chatwoot/Chatwoot'
import ConfirmContextProvider from 'HOOKS/useConfirm/components/ConfirmContextProvider/ConfirmContextProvider'
import { FeatureFlagsProvider } from 'HOOKS/useFeatureFlags/components/FeatureFlagsProvider/FeatureFlagsProvider'
import { ShouldShowWelcomePageProvider } from 'HOOKS/useShouldShowWelcomePage/ShouldShowWelcomePageProvider'
import useSourceAccounts from 'HOOKS/useSourceAccounts'
import TimeSchedulerProvider from 'HOOKS/useTimeScheduler/components/TimeSchedulerProvider/TimeSchedulerProvider'
import { TimezoneProvider } from 'HOOKS/useTimezone/components/TimezoneProvider/TimezoneProvider'
import CreditsTopUpModal from 'PAGES/Settings/components/PaymentContainer/components/CreditsContainer/CreditsTopUpModal'
import { selectSession } from 'STORE/auth/authSlice'
import { useUserQuery } from 'STORE/endpoints/root'

import { useAppSelector } from '../store'
import { ErrorFallback } from './ErrorFallback'
import Header from './Header'
import { MobileMenuProvider } from './mobileMenu/MobileMenuContext'
import RootLayout from './RootLayout'
import { RouteErrorFallback } from './RouteErrorFallback'
import { loadRouteModule } from './staleChunk'

const page =
  (load: () => Promise<{ default: ComponentType }>): LazyRouteFunction<RouteObject> =>
  async () => ({ Component: (await loadRouteModule(load)).default })

const RequiredSession: FC = () => {
  const session = useAppSelector(selectSession)

  if (session === undefined || Boolean(session.isExpired)) {
    return <Navigate to="/login" />
  }

  return <Outlet />
}

const UnauthenticatedLayout: FC = () => {
  const { data: user, isSuccess } = useUserQuery()

  if (isSuccess && user !== undefined) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

const AuthorizedLayout: FC = () => {
  return (
    <FeatureFlagsProvider>
      <ShouldShowWelcomePageProvider>
        <TimezoneProvider>
          <TimeSchedulerProvider>
            <ConfirmContextProvider>
              <AuthorizedLayoutContent />
            </ConfirmContextProvider>
          </TimeSchedulerProvider>
        </TimezoneProvider>
        <ChatwootLoader />
      </ShouldShowWelcomePageProvider>
    </FeatureFlagsProvider>
  )
}

const AuthorizedLayoutContent: FC = () => {
  const { pathname } = useLocation()
  const { sourceAccounts, isLoading: isSourceAccountsLoading } = useSourceAccounts()

  const hasEmptySourceAccounts =
    !isSourceAccountsLoading && sourceAccounts !== undefined && sourceAccounts.length === 0

  if (pathname === '/connect-account') {
    return hasEmptySourceAccounts || isSourceAccountsLoading ? (
      <Outlet />
    ) : (
      <Navigate to="/posts?status=SENT" replace />
    )
  }

  if (hasEmptySourceAccounts) {
    return <Navigate to="/connect-account" replace />
  }

  return (
    <MobileMenuProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          height: '100%',
          width: '100%',
        }}
      >
        <Header />
        <CreditsTopUpModal />
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error, info) => {
            Sentry.captureException(error, {
              extra: { componentStack: info.componentStack },
            })
          }}
        >
          <Outlet />
        </ErrorBoundary>
      </Box>
    </MobileMenuProvider>
  )
}

export const browserRouter: any = createBrowserRouter([
  {
    Component: RootLayout,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        Component: RequiredSession,
        children: [
          {
            Component: AuthorizedLayout,
            children: [
              {
                errorElement: <RouteErrorFallback />,
                children: [
                  { path: '/', element: <Navigate to="/posts?status=SENT" /> },
                  {
                    path: 'posts/:id/preview',
                    lazy: page(async () => await import('../pages/Post/PostPreviewPage')),
                  },
                  {
                    path: 'posts/:id',
                    lazy: page(async () => await import('../pages/Post/PostPage')),
                  },
                  {
                    path: 'posts/*',
                    lazy: page(async () => await import('../pages/Posts/PostsPage')),
                  },
                  {
                    path: 'campaigns/*',
                    lazy: page(async () => await import('../pages/Campaigns/CampaignsPage')),
                  },
                  {
                    path: 'campaigns/:id',
                    lazy: page(async () => await import('../pages/Campaign/CampaignPage')),
                  },
                  {
                    path: 'settings/',
                    lazy: page(async () => await import('../pages/Settings/SettingsPage')),
                  },
                  {
                    path: 'privacy',
                    lazy: page(async () => await import('../pages/Privacy/PrivacyPage')),
                  },
                  {
                    path: 'welcome',
                    lazy: page(async () => await import('../pages/Welcome/WelcomePage')),
                  },
                  {
                    path: 'connect-account',
                    lazy: page(
                      async () => await import('../pages/ConnectAccount/ConnectAccountPage'),
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        Component: UnauthenticatedLayout,
        children: [
          { path: 'login', lazy: page(async () => await import('../pages/Login/LoginPage')) },
        ],
      },
      { path: '/', element: <Navigate to="/login" /> },
      { path: 'privacy', lazy: page(async () => await import('../pages/Privacy/PrivacyPage')) },
      { path: 'sign-in', element: <Navigate to="/login" /> },
      { path: 'sign-up', element: <Navigate to="/login" /> },
      { path: '/*', lazy: page(async () => await import('../pages/NotFound/NotFoundPage')) },
    ],
  },
])

const Router = (): ReactElement => (
  <RouterProvider router={browserRouter} future={{ v7_startTransition: true }} />
)

export default Router
